import type { AxiosError } from 'axios';
import { Button, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { PriceListItem } from '@/api/generated/models';
import {
  getListItemsQueryKey,
  useAddItem,
  useUpdateItem
} from '@/api/generated/price-lists/price-lists';
import { useListUnits } from '@/api/generated/units/units';
import {
  CreatableSelectField,
  FormModal,
  ModalFooter,
  MutationButton,
  NumberField,
  SelectField,
  TextField,
  Toast
} from '@/components/common';
import { activeUnitOptions } from '@/features/units/unitDisplay';

const baseSchema = z.object({
  sortOrder: z.number().nullable(),
  itemCode: z.string().min(1),
  category: z.string().min(1),
  unitId: z.string().min(1),
  description: z.string().min(1),
  unitPrice: z.number().positive(),
  normHours: z.number().nullable(),
  isAdditionalWork: z.enum(['no', 'yes'])
});

type FormValues = z.infer<typeof baseSchema>;

const emptyValues: FormValues = {
  sortOrder: null,
  itemCode: '',
  category: '',
  unitId: '',
  description: '',
  unitPrice: 0,
  normHours: null,
  isAdditionalWork: 'no'
};

// Isti obrazac kao RegisterForm/ConnectForm/PartnerLinkingPage — backend već
// vraća specifičan, lokalizirani razlog (npr. "Šifra stavke već postoji...").
const itemErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

interface PriceListItemFormModalProps {
  opened: boolean;
  onClose: () => void;
  priceListId: string;
  priceListName: string;
  item?: PriceListItem;
  nextSortOrder?: number;
  existingItemCodes?: string[];
  existingCategories?: string[];
  onSaved?: (message: string) => void;
}

export const PriceListItemFormModal = ({
  opened,
  onClose,
  priceListId,
  priceListName,
  item,
  nextSortOrder,
  existingItemCodes = [],
  existingCategories = [],
  onSaved
}: PriceListItemFormModalProps) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = !!item;

  const { data: unitsData } = useListUnits();
  const unitOptions = activeUnitOptions(unitsData, i18n.language, item?.unitId);

  // Duplikat šifre mora biti dio SHEME (superRefine), ne provjera unutar
  // handleSubmit — Mantine poziva handleSubmit tek nakon što zodResolver
  // prođe, pa bi provjera unutar handleSubmit ostala neizvršena kad god su
  // druga polja (npr. kategorija) i dalje nevaljana. Backend je i dalje
  // konačan izvor istine (uq_price_list_items_code) — ovo je samo brža
  // povratna informacija bez mrežnog poziva.
  const schema = baseSchema.superRefine((data, ctx) => {
    const code = data.itemCode.trim();
    if (code !== (item?.itemCode ?? '') && existingItemCodes.includes(code)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['itemCode'],
        message: t('priceLists.itemCodeDuplicate')
      });
    }
  });

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: emptyValues
  });

  useEffect(() => {
    if (opened) {
      form.setValues(
        item
          ? {
              sortOrder: item.sortOrder ?? null,
              itemCode: item.itemCode ?? '',
              category: item.category ?? '',
              unitId: item.unitId ?? '',
              description: item.description ?? '',
              unitPrice: item.unitPrice ?? 0,
              normHours: item.normHours ?? null,
              isAdditionalWork: item.isAdditionalWork ? 'yes' : 'no'
            }
          : emptyValues
      );
    }
  }, [opened, item]);

  const invalidateItems = () =>
    queryClient.invalidateQueries({ queryKey: getListItemsQueryKey(priceListId) });

  const addMutation = useAddItem({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        invalidateItems();
        onSaved?.(t('priceLists.itemAddSuccess'));
        onClose();
      }
    }
  });

  const updateMutation = useUpdateItem({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        invalidateItems();
        onSaved?.(t('priceLists.itemUpdateSuccess'));
        onClose();
      }
    }
  });

  const mutation = isEdit ? updateMutation : addMutation;

  const handleSubmit = (values: FormValues) => {
    if (isEdit && item?.id) {
      updateMutation.mutate({
        id: priceListId,
        itemId: item.id,
        data: {
          itemCode: values.itemCode,
          category: values.category,
          description: values.description,
          unitId: values.unitId,
          unitPrice: values.unitPrice,
          normHours: values.normHours ?? undefined,
          isAdditionalWork: values.isAdditionalWork === 'yes',
          sortOrder: values.sortOrder ?? undefined
        }
      });
    } else {
      addMutation.mutate({
        id: priceListId,
        data: {
          itemCode: values.itemCode,
          category: values.category,
          description: values.description,
          unitId: values.unitId,
          unitPrice: values.unitPrice,
          normHours: values.normHours ?? undefined,
          isAdditionalWork: values.isAdditionalWork === 'yes',
          sortOrder: nextSortOrder
        }
      });
    }
  };

  return (
    <FormModal
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={4}>
          <Text fz={20} fw={700} style={{ color: 'var(--color-text-primary)' }}>
            {isEdit ? t('priceLists.editItem') : t('priceLists.addItem')}
          </Text>
          <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
            {t(isEdit ? 'priceLists.editItemIntro' : 'priceLists.addItemIntro', {
              name: priceListName
            })}
          </Text>
        </Stack>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap={16}>
          {mutation.isError && (
            <Toast tone="danger">{itemErrorMessage(mutation.error, t('common.error'))}</Toast>
          )}

          <div className="form-row-2col">
            {isEdit && (
              <div style={{ flex: 1 }}>
                <NumberField
                  label={t('priceLists.fieldSortOrder')}
                  allowNegative={false}
                  allowDecimal={false}
                  {...form.getInputProps('sortOrder')}
                />
              </div>
            )}
            <div style={{ flex: 1 }}>
              <TextField
                label={t('priceLists.fieldItemCode')}
                required
                helperText={isEdit ? t('priceLists.itemCodeEditHint') : undefined}
                {...form.getInputProps('itemCode')}
              />
            </div>
          </div>

          <div className="form-row-2col">
            <div style={{ flex: 1 }}>
              <CreatableSelectField
                label={t('priceLists.colCategory')}
                required
                placeholder={t('priceLists.categoryPlaceholder')}
                data={existingCategories}
                createLabel={(query) => t('priceLists.categoryCreate', { value: query })}
                emptyLabel={t('priceLists.categoryEmpty')}
                {...form.getInputProps('category')}
              />
            </div>
            <div style={{ flex: 1 }}>
              <SelectField
                label={t('priceLists.colItemUnit')}
                required
                allowDeselect={false}
                placeholder={t('priceLists.unitPlaceholder')}
                data={unitOptions}
                {...form.getInputProps('unitId')}
              />
            </div>
          </div>

          <TextField
            label={t('priceLists.fieldDescription')}
            required
            {...form.getInputProps('description')}
          />

          <div className="form-row-2col">
            <div style={{ flex: 1 }}>
              <NumberField
                label={t('priceLists.fieldUnitPrice')}
                required
                allowNegative={false}
                decimalScale={2}
                fixedDecimalScale
                {...form.getInputProps('unitPrice')}
              />
            </div>
            <div style={{ flex: 1 }}>
              <NumberField
                label={t('priceLists.fieldNormHours')}
                allowNegative={false}
                decimalScale={2}
                {...form.getInputProps('normHours')}
              />
            </div>
          </div>

          <SelectField
            label={t('priceLists.fieldAdditionalWork')}
            allowDeselect={false}
            data={[
              { value: 'no', label: t('priceLists.additionalWorkNo') },
              { value: 'yes', label: t('priceLists.additionalWorkYes') }
            ]}
            {...form.getInputProps('isAdditionalWork')}
          />

          <ModalFooter>
            <Button variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <MutationButton
              type="submit"
              isPending={mutation.isPending}
              isPaused={mutation.isPaused}
            >
              {t('priceLists.saveItem')}
            </MutationButton>
          </ModalFooter>
        </Stack>
      </form>
    </FormModal>
  );
};
